import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { useContent, useContentRefresh } from "../content-context";
import API from "../api";

const euro = (n) => `€${Number(n).toLocaleString("en-IE", { maximumFractionDigits: 2 })}`;

const STATUS_STYLES = {
  confirmed: "border-green-400/50 text-green-300",
  pending: "border-amber-400/50 text-amber-300",
  cancelled: "border-red-400/50 text-red-300",
};

const slug = (name) =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Math.random().toString(36).slice(2, 6);

const inputCls =
  "w-full border-b border-ivory/25 bg-transparent py-1.5 text-sm font-light text-ivory outline-none focus:border-gold";

export default function ActivityManager() {
  const content = useContent();
  const refresh = useContentRefresh();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");
  const [rows, setRows] = useState(null); // catalogue draft
  const [savingRows, setSavingRows] = useState(false);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/activity-bookings`, { credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setBookings(data.bookings || []);
    } catch {
      toast.error("Could not load bookings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const catalogue = rows !== null ? rows : JSON.parse(JSON.stringify((content && content.activities) || []));

  const setRow = (i, key, value) => {
    setRows(() => {
      const copy = JSON.parse(JSON.stringify(catalogue));
      copy[i][key] = value;
      return copy;
    });
  };

  const addRow = () => {
    setRows(() => [
      ...catalogue,
      { id: slug("New activity"), name: "New activity", blurb: "Describe the adventure.", price: 40, deposit: 15, mode: "deposit", active: false },
    ]);
  };

  const removeRow = (i) => {
    setRows(() => catalogue.filter((_, idx) => idx !== i));
  };

  const saveCatalogue = async () => {
    setSavingRows(true);
    try {
      const cleaned = catalogue.map((r) => ({
        ...r,
        price: Number(r.price) || 0,
        deposit: Number(r.deposit) || 0,
        active: !!r.active,
      }));
      const res = await fetch(`${API}/api/admin/content`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: { ...content, activities: cleaned } }),
      });
      if (!res.ok) throw new Error();
      await refresh();
      setRows(null);
      toast.success("Activity catalogue saved — the website is updated.");
    } catch {
      toast.error("Could not save the catalogue.");
    } finally {
      setSavingRows(false);
    }
  };

  const setBookingStatus = async (b, status) => {
    setBusyId(b.id);
    try {
      const res = await fetch(`${API}/api/admin/activity-bookings/${b.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      toast.success(`Booking ${status}.`);
      await loadBookings();
    } catch {
      toast.error("Could not update the booking.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div className="pb-20">
      {/* Bookings */}
      <div className="mt-6 flex items-center justify-between gap-4">
        <p className="text-xs uppercase tracking-widest2 text-coastal" data-testid="activity-bookings-count">
          {bookings.length} {bookings.length === 1 ? "booking" : "bookings"} · newest first
        </p>
        <button
          onClick={loadBookings}
          data-testid="activity-bookings-refresh"
          className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[11px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>
      <div className="mt-6 flex flex-col gap-4" data-testid="activity-bookings-list">
        {!loading && bookings.length === 0 && (
          <p className="mt-8 text-center font-display text-xl italic text-ivory/50" data-testid="activity-bookings-empty">
            No activity bookings yet — the first one appears here the moment it's paid.
          </p>
        )}
        {bookings.map((b) => (
          <div key={b.id} className="border border-ivory/15 bg-navy-2 p-5" data-testid={`activity-booking-row-${b.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="font-display text-xl text-ivory">
                  {b.activity_name} · {b.booking_date}
                </p>
                <p className="mt-1 text-sm font-light text-ivory/80">
                  {b.guests} {b.guests === 1 ? "guest" : "guests"} · {euro(b.amount)} paid ({b.payment_mode}) · {b.name} · {b.email} · {b.phone}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest2 ${STATUS_STYLES[b.status] || "border-ivory/30 text-ivory/60"}`} data-testid={`activity-booking-status-${b.id}`}>
                  {b.status}
                </span>
                {b.status === "confirmed" ? (
                  <button
                    onClick={() => setBookingStatus(b, "cancelled")}
                    disabled={busyId === b.id}
                    data-testid={`activity-booking-cancel-${b.id}`}
                    className="border border-red-400/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-red-300 transition-colors hover:bg-red-400/10 disabled:opacity-60"
                  >
                    {busyId === b.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Cancel"}
                  </button>
                ) : (
                  b.status === "pending" && (
                    <button
                      onClick={() => setBookingStatus(b, "confirmed")}
                      disabled={busyId === b.id}
                      data-testid={`activity-booking-confirm-${b.id}`}
                      className="border border-gold/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-gold-2 transition-colors hover:bg-gold hover:text-navy disabled:opacity-60"
                    >
                      Confirm
                    </button>
                  )
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Catalogue editor */}
      <div className="mt-12 border border-ivory/15 bg-navy-2 p-8" data-testid="activity-catalogue">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h3 className="font-display text-2xl text-ivory">Activities on sale</h3>
          <div className="flex gap-3">
            <button
              onClick={addRow}
              data-testid="activity-add-button"
              className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-ivory/80 transition-colors hover:border-gold hover:text-gold-2"
            >
              <Plus className="h-3.5 w-3.5" /> Add activity
            </button>
            <button
              onClick={saveCatalogue}
              disabled={savingRows}
              data-testid="activity-save-catalogue"
              className="flex items-center gap-2 bg-gold px-6 py-2 text-[10px] font-semibold uppercase tracking-widest2 text-navy transition-colors hover:bg-gold-2 disabled:opacity-60"
            >
              {savingRows ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              {savingRows ? "Saving…" : "Save & publish"}
            </button>
          </div>
        </div>
        <p className="mt-2 text-xs font-light text-ivory/60">
          Set each activity's full price, and the deposit guests pay today. Inactive activities are hidden from the site.
        </p>
        <div className="mt-6 flex flex-col gap-6">
          {catalogue.map((a, i) => (
            <div key={a.id || i} className="border border-ivory/10 p-5" data-testid={`activity-catalogue-row-${i}`}>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Name</span>
                  <input value={a.name || ""} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`activity-name-${i}`} className={inputCls} />
                </label>
                <label className="block sm:col-span-2">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Description</span>
                  <input value={a.blurb || ""} onChange={(e) => setRow(i, "blurb", e.target.value)} data-testid={`activity-blurb-${i}`} className={inputCls} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Full price (€ per person)</span>
                  <input value={a.price ?? ""} onChange={(e) => setRow(i, "price", e.target.value)} inputMode="decimal" data-testid={`activity-price-${i}`} className={inputCls} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Deposit (€ per person)</span>
                  <input value={a.deposit ?? ""} onChange={(e) => setRow(i, "deposit", e.target.value)} inputMode="decimal" data-testid={`activity-deposit-${i}`} className={inputCls} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-widest2 text-ivory/55">Guests pay</span>
                  <select value={a.mode || "deposit"} onChange={(e) => setRow(i, "mode", e.target.value)} data-testid={`activity-mode-${i}`} className={inputCls}>
                    <option value="deposit" className="bg-navy text-ivory">Deposit now, balance on the day</option>
                    <option value="full" className="bg-navy text-ivory">Full price now</option>
                  </select>
                </label>
                <label className="flex items-center gap-3 pt-5">
                  <input type="checkbox" checked={a.active !== false} onChange={(e) => setRow(i, "active", e.target.checked)} data-testid={`activity-active-${i}`} className="h-4 w-4 accent-[#c9a227]" />
                  <span className="text-sm font-light text-ivory/80">Show on the website</span>
                </label>
              </div>
              <button
                onClick={() => removeRow(i)}
                data-testid={`activity-remove-${i}`}
                className="mt-4 flex items-center gap-2 border border-red-400/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest2 text-red-300 transition-colors hover:bg-red-400/10"
              >
                <Trash2 className="h-3 w-3" /> Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
